const INDENT_CLASS_PREFIX = 'ql-indent-';
const EMAIL_ORDERED_LIST_STYLE =
  'margin-top:0 !important;margin-bottom:0 !important;padding-top:0 !important;padding-bottom:0 !important;padding-left:2.25em !important;';
const EMAIL_BULLET_LIST_STYLE =
  'margin-top:0 !important;margin-bottom:0 !important;padding-top:0 !important;padding-bottom:0 !important;padding-left:2.25em !important;';
const EMAIL_LIST_ITEM_STYLE = 'margin:0 !important;padding:0 !important;';

type SemanticListType = 'ol' | 'ul';

interface QuillListItemDescriptor {
  element: HTMLLIElement;
  indent: number;
  type: SemanticListType;
}

export const normalizeQuillHtmlForEmail = (html: string): string => {
  if (!html) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const quillLists = Array.from(doc.body.querySelectorAll('ol, ul')).filter((listNode) =>
    isQuillManagedList(listNode as HTMLOListElement | HTMLUListElement)
  );

  quillLists.forEach((listNode) => {
    const normalizedList = buildSemanticListTree(
      doc,
      getListDescriptors(listNode as HTMLOListElement | HTMLUListElement)
    );

    listNode.replaceWith(normalizedList);
  });

  doc.body.querySelectorAll('.ql-ui').forEach((node) => node.remove());
  return doc.body.innerHTML;
};

const isQuillManagedList = (listNode: HTMLOListElement | HTMLUListElement): boolean =>
  getDirectListItems(listNode).some(
    (item) => item.hasAttribute('data-list') || getIndentLevel(item) > 0 || !!item.querySelector('.ql-ui')
  );

const getDirectListItems = (listNode: HTMLOListElement | HTMLUListElement): HTMLLIElement[] =>
  Array.from(listNode.children).filter((child): child is HTMLLIElement => child.tagName === 'LI');

const getListDescriptors = (listNode: HTMLOListElement | HTMLUListElement): QuillListItemDescriptor[] =>
  getDirectListItems(listNode).map((item) => ({
    element: item,
    indent: getIndentLevel(item),
    type: resolveListType(item, listNode.tagName.toLowerCase() as SemanticListType)
  }));

const getIndentLevel = (item: HTMLLIElement): number => {
  const indentClass = Array.from(item.classList).find((className) =>
    className.startsWith(INDENT_CLASS_PREFIX)
  );
  if (!indentClass) return 0;

  const indentValue = Number(indentClass.slice(INDENT_CLASS_PREFIX.length));
  return Number.isFinite(indentValue) && indentValue > 0 ? indentValue : 0;
};

const resolveListType = (item: HTMLLIElement, fallbackType: SemanticListType): SemanticListType => {
  const dataListType = item.getAttribute('data-list');
  if (dataListType === 'ordered') return 'ol';
  if (dataListType === 'bullet' || dataListType === 'checked' || dataListType === 'unchecked') {
    return 'ul';
  }

  return fallbackType;
};

const buildSemanticListTree = (doc: Document, descriptors: QuillListItemDescriptor[]): DocumentFragment => {
  const fragment = doc.createDocumentFragment();
  const stack: Array<{
    level: number;
    type: SemanticListType;
    list: HTMLOListElement | HTMLUListElement;
    lastItem: HTMLLIElement | null;
  }> = [];

  descriptors.forEach((descriptor) => {
    const normalizedLevel = stack.length ? Math.min(descriptor.indent, stack.length) : 0;

    while (stack.length > normalizedLevel + 1) {
      stack.pop();
    }

    if (stack.length > normalizedLevel) {
      stack.length = normalizedLevel + 1;
    }

    const parentContainer =
      normalizedLevel === 0 ? fragment : stack[normalizedLevel - 1]?.lastItem || fragment;

    const currentStackEntry = stack[normalizedLevel];
    const shouldCreateList =
      !currentStackEntry ||
      currentStackEntry.type !== descriptor.type ||
      currentStackEntry.list.parentNode !== parentContainer;

    if (shouldCreateList) {
      const list = doc.createElement(descriptor.type) as HTMLOListElement | HTMLUListElement;
      appendInlineStyle(list, descriptor.type === 'ol' ? EMAIL_ORDERED_LIST_STYLE : EMAIL_BULLET_LIST_STYLE);
      parentContainer.appendChild(list);
      stack[normalizedLevel] = {
        level: normalizedLevel,
        type: descriptor.type,
        list,
        lastItem: null
      };
      stack.length = normalizedLevel + 1;
    }

    const listItem = cloneListItemForEmail(descriptor.element);
    stack[normalizedLevel].list.appendChild(listItem);
    stack[normalizedLevel].lastItem = listItem;
  });

  return fragment;
};

const cloneListItemForEmail = (item: HTMLLIElement): HTMLLIElement => {
  const clone = item.cloneNode(true) as HTMLLIElement;

  clone.removeAttribute('data-list');
  appendInlineStyle(clone, EMAIL_LIST_ITEM_STYLE);
  Array.from(clone.classList)
    .filter((className) => className.startsWith(INDENT_CLASS_PREFIX) || className === 'ql-direction-rtl')
    .forEach((className) => clone.classList.remove(className));

  if (!clone.classList.length) {
    clone.removeAttribute('class');
  }

  clone.querySelectorAll('.ql-ui').forEach((node) => node.remove());
  Array.from(clone.children)
    .filter((child) => child.tagName === 'OL' || child.tagName === 'UL')
    .forEach((child) => child.remove());

  return clone;
};

const appendInlineStyle = (element: HTMLElement, styleValue: string): void => {
  const currentStyle = element.getAttribute('style');
  element.setAttribute('style', currentStyle ? `${currentStyle};${styleValue}` : styleValue);
};
